package com.evoapps.evoshift.domain.schedule

import com.evoapps.evoshift.domain.model.TimeRange
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.number
import kotlinx.datetime.plus

/** SH-114: where "Copy from…" in the roster builder takes the shifts. */
sealed interface CopySource {
    /**
     * The seven days from [sourceStart] onto the seven days from [targetStart] ("last week" is the
     * week before the target). Both start on the same weekday, so each day keeps its weekday.
     */
    data class Week(val sourceStart: LocalDate, val targetStart: LocalDate) : CopySource {
        init {
            require(sourceStart.dayOfWeek == targetStart.dayOfWeek) { "Weeks start on different weekdays" }
            require(sourceStart != targetStart) { "A week cannot be copied onto itself" }
        }
    }

    /** The month before the roster's, mapped by weekday (see [RosterCopyMapping.previousMonth]). */
    data object PreviousMonth : CopySource

    /**
     * SH-217: shifts read from a spreadsheet (the roster import), landing on their own [days]; there
     * is no source month. Written through the copy tool for its Add / Replace / Skip and Undo.
     */
    data class Imported(val days: Set<LocalDate>) : CopySource

    /**
     * SH-171: a block of days ([sources], contiguous or not) pasted from each of [starts]: the first
     * source day lands on the start and the others keep their distance from it (Mon 3 – Wed 5 from
     * Mon 17 → 17, 18, 19; Fri 7 + Sun 9 from Fri 21 → 21 and 23). One day onto several days is the
     * case of a single source ([day]). A day is never copied onto itself.
     */
    data class Days(
        val sources: Set<LocalDate>,
        val starts: Set<LocalDate>,
        /**
         * SH-205: the block is repeated up to the month's end ([CopySource.repeat]): its last
         * repetition is cut at the month's end on purpose, so those days are not reported as
         * left outside the month.
         */
        val clipToMonth: Boolean = false
    ) : CopySource {
        init {
            require(sources.isNotEmpty()) { "Nothing to copy" }
        }

        /** The first source day: the one each start receives. */
        val anchor: LocalDate get() = sources.min()

        /**
         * Every target → its source, all months, in order. Where two pasted blocks overlap, the
         * earlier start wins the day; a day landing on its own source is left out.
         */
        fun mapping(): Map<LocalDate, LocalDate> {
            val anchorDays = anchor.toEpochDays()
            val result = linkedMapOf<LocalDate, LocalDate>()
            starts.sorted().forEach { start ->
                val shift = start.toEpochDays() - anchorDays
                sources.sorted().forEach { source ->
                    val target = LocalDate.fromEpochDays(source.toEpochDays() + shift)
                    if (target != source && target !in result) result[target] = source
                }
            }
            return result.entries.sortedBy { it.key }.associate { it.key to it.value }
        }
    }

    companion object {
        /** SH-171: one day onto each of [targets] ("Copy day"). */
        fun day(source: LocalDate, targets: Set<LocalDate>): Days = Days(setOf(source), targets)

        /**
         * SH-205: "Repeat until the month ends": the block [sources] (a few days, a week, any
         * range, also from an earlier month) pasted from [start], then again right after it, back
         * to back, while a repetition starts on or before [until] (the roster month's last day);
         * the last one is cut there.
         */
        fun repeat(sources: Set<LocalDate>, start: LocalDate, until: LocalDate): Days {
            require(sources.isNotEmpty()) { "Nothing to copy" }
            val span = (sources.max().toEpochDays() - sources.min().toEpochDays() + 1).toInt()
            val starts = generateSequence(start) { it.plus(DatePeriod(days = span)) }.takeWhile { it <= until }.toSet()
            return Days(sources, starts, clipToMonth = true)
        }
    }
}

/** SH-114: which day of the source each day of the roster's month is copied from. Pure date logic. */
object RosterCopyMapping {

    /** Target day → source day for [source] into the roster month [year]-[month]; only days of that month. */
    fun days(source: CopySource, year: Int, month: Int): Map<LocalDate, LocalDate> = when (source) {
        is CopySource.Week -> week(source, year, month)
        CopySource.PreviousMonth -> previousMonth(year, month)
        is CopySource.Days -> days(source, year, month)
        is CopySource.Imported -> source.days.filter { it.year == year && it.month.number == month }.sorted().associateWith { it }
    }

    /** SH-171: each pasted day of the roster's month → its source day, in order (see [CopySource.Days.mapping]). */
    fun days(source: CopySource.Days, year: Int, month: Int): Map<LocalDate, LocalDate> =
        source.mapping().filterKeys { it.year == year && it.month.number == month }

    /** SH-171: the pasted days that would fall outside the roster's month (not copied), in order. */
    fun outsideMonth(source: CopySource, year: Int, month: Int): List<LocalDate> = when (source) {
        is CopySource.Days -> if (source.clipToMonth) emptyList() else source.mapping().keys.filter { it.year != year || it.month.number != month }
        else -> emptyList()
    }

    /** The week's days that fall in the roster's month (a week across the 1st is cut). */
    fun week(source: CopySource.Week, year: Int, month: Int): Map<LocalDate, LocalDate> =
        (0 until 7).associate { offset ->
            source.targetStart.plus(DatePeriod(days = offset)) to source.sourceStart.plus(DatePeriod(days = offset))
        }.filterKeys { it.year == year && it.month.number == month }

    /**
     * By weekday: the month's n-th Monday is copied from the previous month's n-th Monday, and so
     * on. A fifth occurrence the previous month does not have (a 31-day month after February) is
     * copied from its last one, so every day of the month has a source.
     */
    fun previousMonth(year: Int, month: Int): Map<LocalDate, LocalDate> {
        val first = LocalDate(year, month, 1)
        val previous = first.plus(DatePeriod(months = -1))
        val sourceByWeekday = monthDays(previous).groupBy { it.dayOfWeek }
        return monthDays(first).associateWith { day ->
            val occurrences = sourceByWeekday.getValue(day.dayOfWeek)
            occurrences[minOf((day.day - 1) / 7, occurrences.size - 1)]
        }
    }

    /** [range] moved by [days] (both ends, so an overnight shift stays overnight); the times are kept. */
    fun moved(range: TimeRange, days: Long): TimeRange = TimeRange(moved(range.start, days), moved(range.end, days))

    private fun moved(iso: String, days: Long): String {
        if (iso.length < 10) return iso
        val date = LocalDate.parse(iso.take(10)).plus(DatePeriod(days = days.toInt()))
        return date.toString() + iso.drop(10)
    }

    private fun monthDays(first: LocalDate): List<LocalDate> =
        generateSequence(first) { it.plus(DatePeriod(days = 1)) }.takeWhile { it.month == first.month }.toList()
}
